/**
 * UserMenu Component
 * Displays login button when logged out, or user avatar with dropdown when logged in
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown, Avatar, Tag, Spin } from 'antd';
import { 
  UserOutlined, 
  LoginOutlined, 
  LogoutOutlined,
  CrownOutlined,
  EditOutlined,
  TeamOutlined,
  BookOutlined,
  ReadOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { AppRoles } from '../../services/authService';
import { getProfileImageUrl } from '../../utils/urlUtils';
import styles from './UserMenu.module.css';

/**
 * Get role display configuration
 */
const getRoleConfig = (role) => {
  const configs = {
    [AppRoles.Admin]: { 
      label: 'Admin', 
      color: '#f5222d', 
      icon: <CrownOutlined /> 
    },
    [AppRoles.ContentCreator]: { 
      label: 'Content Creator', 
      color: '#722ed1', 
      icon: <EditOutlined /> 
    },
    [AppRoles.MasterTrainer]: { 
      label: 'Master Trainer', 
      color: '#fa8c16', 
      icon: <TeamOutlined /> 
    },
    [AppRoles.Trainer]: { 
      label: 'Trainer', 
      color: '#1890ff', 
      icon: <BookOutlined /> 
    },
    [AppRoles.Trainee]: { 
      label: 'Trainee', 
      color: '#52c41a', 
      icon: <ReadOutlined /> 
    },
  };
  return configs[role] || { label: role, color: '#8c8c8c', icon: <UserOutlined /> };
};

/**
 * Get user initials from name
 */
const getInitials = (firstName, lastName, email) => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name
 */
const getDisplayName = (user) => {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.email?.split('@')[0] || 'User';
};

/**
 * UserMenu Component
 * Shows login button or user dropdown based on authentication state
 */
const UserMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout } = useAuth();

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Generate avatar color based on user email/id
  const avatarColor = useMemo(() => {
    if (!user?.email) return '#1890ff';
    const colors = ['#1890ff', '#13c2c2', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96'];
    const hash = user.email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }, [user?.email]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="small" />
      </div>
    );
  }

  // Not authenticated - show login button
  if (!isAuthenticated || !user) {
    return (
      <button 
        className={styles.loginButton} 
        onClick={handleLogin}
        aria-label={t('auth.signIn')}
      >
        <LoginOutlined className={styles.loginIcon} />
        <span className={styles.loginText}>{t('auth.signIn')}</span>
      </button>
    );
  }

  // Authenticated - show user dropdown
  const initials = getInitials(user.firstName, user.lastName, user.email);
  const displayName = getDisplayName(user);
  const roles = user.roles || [];
  
  // Profile image URL
  const profileImageUrl = getProfileImageUrl(user);

  const menuItems = [
    {
      key: 'header',
      type: 'group',
      label: (
        <div className={styles.menuHeader}>
          <div className={styles.userName}>{displayName}</div>
          <div className={styles.userEmail}>{user.email}</div>
        </div>
      ),
    },
    {
      key: 'roles',
      type: 'group',
      children: roles.length > 0 ? roles.map(role => {
        const config = getRoleConfig(role);
        return {
          key: `role-${role}`,
          label: (
            <Tag 
              icon={config.icon} 
              color={config.color}
              className={styles.roleTag}
            >
              {config.label}
            </Tag>
          ),
          disabled: true,
        };
      }) : [{
        key: 'no-roles',
        label: <span className={styles.noRoles}>{t('auth.noRoles')}</span>,
        disabled: true,
      }],
    },
    {
      key: 'divider-1',
      type: 'divider',
    },
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: t('auth.profile'),
      onClick: () => navigate('/profile'),
    },
    {
      key: 'divider-2',
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.signOut'),
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName={styles.dropdownOverlay}
    >
      <button className={styles.userButton} aria-label={t('auth.userMenu')}>
        {profileImageUrl ? (
          <Avatar 
            size={32} 
            src={profileImageUrl}
            className={styles.avatar}
          />
        ) : (
          <Avatar 
            size={32} 
            style={{ backgroundColor: avatarColor }}
            className={styles.avatar}
          >
            {initials}
          </Avatar>
        )}
        <span className={styles.displayName}>{displayName}</span>
      </button>
    </Dropdown>
  );
};

export default UserMenu;
